/**
 * One-click vocal recording over the song already sitting in the shared engine.
 * idle → arming → recording → processing → review. Keeps up to 5 takes in memory.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { checkMicAvailability } from "@/lib/micCheck";
import { showMicHelp, startRecordingWithTimeout } from "@/lib/micHelp";
import { analyserPeak, estimateHeadTrim, trimHead } from "@/lib/creatorVocal";

const MAX_TAKES = 5;

export default function useVocalRecorder({ engine, hasBacking, songDuration, takeName, deviceId }) {
  const [phase, setPhase] = useState("idle");
  const [vocalChannel, setVocalChannel] = useState(null);
  const [takes, setTakes] = useState([]);
  const [activeTakeId, setActiveTakeId] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const timesRef = useRef({ tRec: 0, tPlay: 0, startedAt: 0 });
  const prevRef = useRef({ autoTrim: undefined, mode: undefined });
  const tickRef = useRef(null);
  const stopRef = useRef(null);
  const channelRef = useRef(null);

  const clearTick = () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };
  useEffect(() => () => clearTick(), []);

  const ensureChannel = useCallback(() => {
    if (channelRef.current != null) return channelRef.current;
    const [idx] = engine.freeChannelIndices(1);
    if (idx == null) throw new Error("The studio is full — clear a channel in the Advanced Studio first.");
    channelRef.current = idx;
    setVocalChannel(idx);
    return idx;
  }, [engine]);

  const adoptChannel = useCallback((idx, buffer, meta = {}) => {
    channelRef.current = idx;
    setVocalChannel(idx);
    const take = { id: `restored-${Date.now()}`, buffer, blob: null, headSec: meta.trim_head_sec || 0, durationSec: buffer.duration, at: meta.at || new Date().toISOString(), kept: true, recordingId: meta.recording_id || null };
    setTakes([take]);
    setActiveTakeId(take.id);
    setPhase("review");
  }, []);

  const start = useCallback(async () => {
    if (phase === "recording" || phase === "arming" || phase === "processing") return;
    const pre = checkMicAvailability();
    if (pre) { showMicHelp(pre, () => start()); return; }
    setPhase("arming");
    let idx;
    try { idx = ensureChannel(); } catch (e) { toast.error(e.message); setPhase(takes.length ? "review" : "idle"); return; }
    prevRef.current = { autoTrim: engine.autoTrimSilence, mode: engine.recordingMode };
    engine.autoTrimSilence = false;      // alignment matters more than a tidy head
    engine.recordingMode = "dry";
    engine.pause();
    engine.seekTo(0);
    const hadTake = !!engine.channels[idx]?.buffer;
    if (hadTake) engine.setChannelMute(idx, true);   // don't play the old take into the mic
    try {
      await startRecordingWithTimeout(engine, idx, { deviceId: deviceId || null });
    } catch (e) {
      engine.autoTrimSilence = prevRef.current.autoTrim;
      engine.recordingMode = prevRef.current.mode;
      if (hadTake) engine.setChannelMute(idx, false);
      showMicHelp(e, () => start());
      setPhase(takes.length ? "review" : "idle");
      return;
    }
    const ctx = engine.ctx;
    timesRef.current = { tRec: ctx.currentTime, tPlay: ctx.currentTime, startedAt: Date.now() };
    if (hasBacking) {
      await engine.play();
      timesRef.current.tPlay = engine.startTime || ctx.currentTime;
    }
    setElapsed(0); setLevel(0);
    setPhase("recording");
    clearTick();
    tickRef.current = setInterval(() => {
      setElapsed((Date.now() - timesRef.current.startedAt) / 1000);
      setLevel(analyserPeak(engine.recordingAnalyser?.node));
      if (hasBacking && songDuration > 0 && (engine.getPlayhead() >= songDuration + 0.25 || !engine.playing)) {
        stopRef.current?.();
      }
    }, 100);
  }, [phase, engine, ensureChannel, deviceId, hasBacking, songDuration, takes.length]);

  const stop = useCallback(async () => {
    if (phase !== "recording") return;
    clearTick();
    setPhase("processing");
    const idx = channelRef.current;
    engine.pause();
    const res = await engine.stopRecording();
    engine.autoTrimSilence = prevRef.current.autoTrim;
    engine.recordingMode = prevRef.current.mode;
    engine.setChannelMute(idx, false);
    const active = takes.find((t) => t.id === activeTakeId);
    if (!res || res.error || !res.buffer) {
      toast.error("That take didn't record", { description: res?.error?.message || "Try again — check the microphone is not muted.", duration: 9000 });
      if (active?.buffer) engine.loadBufferToChannel(idx, active.buffer, takeName);
      engine.seekTo(0);
      setPhase(takes.length ? "review" : "idle");
      return;
    }
    const head = hasBacking ? estimateHeadTrim(engine.ctx, timesRef.current.tRec, timesRef.current.tPlay) : 0;
    const buffer = trimHead(engine.ctx, res.buffer, head);
    engine.loadBufferToChannel(idx, buffer, takeName);
    engine.seekTo(0);
    const take = { id: `take-${Date.now()}`, buffer, blob: res.blob, headSec: head, durationSec: buffer.duration, at: new Date().toISOString(), kept: false, recordingId: null };
    setTakes((t) => [...t, take].slice(-MAX_TAKES));
    setActiveTakeId(take.id);
    setPhase("review");
  }, [phase, engine, takes, activeTakeId, hasBacking, takeName]);
  stopRef.current = stop;

  const selectTake = useCallback((id) => {
    const t = takes.find((x) => x.id === id);
    if (!t || channelRef.current == null) return;
    engine.pause();
    engine.loadBufferToChannel(channelRef.current, t.buffer, takeName);
    engine.seekTo(0);
    setActiveTakeId(id);
  }, [takes, engine, takeName]);

  const markKept = useCallback((id, recordingId) => {
    setTakes((t) => t.map((x) => (x.id === id ? { ...x, kept: true, recordingId } : x)));
  }, []);

  const activeTake = takes.find((t) => t.id === activeTakeId) || null;
  return { phase, vocalChannel, takes, activeTake, elapsed, level, start, stop, selectTake, markKept, adoptChannel };
}
